import { generateUUID } from '@app/lib/utility/helper';

export type BlendMode = 'normal' | 'multiply' | 'screen' | 'overlay';

// ── SpriteLayer ──────────────────────────────────────────────────────────────
// Global layer definition – lives on the sprite, not on individual frames.
// Pixel data is NOT stored here; it lives in the cel map (FrameState.cels).
export interface SpriteLayer {
  id: string;
  name: string;
  visible: boolean;
  locked: boolean;
  opacity: number;          // 0–100
  blendMode: BlendMode;
  isContinuous: boolean;    // celluloid: hold this layer across frames
  tintColor: string | null; // optional tint shift
  holdDuration: number;     // celluloid: how many frames this cel persists (1 = normal)
}

// Back-compat alias — avoid updating every import site that uses the old name
export type Layer = SpriteLayer;

// ── LayerWithPixels ──────────────────────────────────────────────────────────
// Transient composite helper: a SpriteLayer plus the cel pixels for one frame.
// Used only inside compositeLayers and the reducer – never persisted.
export interface LayerWithPixels extends SpriteLayer {
  pixels: string[][];
}

// ── Factories ────────────────────────────────────────────────────────────────
export function createBlankSpriteLayer(name = 'Layer 1'): SpriteLayer {
  return {
    id: generateUUID(),
    name,
    visible: true,
    locked: false,
    opacity: 100,
    blendMode: 'normal',
    isContinuous: false,
    tintColor: null,
    holdDuration: 1,
  };
}

/** Back-compat: creates a SpriteLayer + a pre-filled pixel grid (for import/AI). */
export function createBlankLayer(width: number, height: number, name = 'Layer 1'): LayerWithPixels {
  return {
    ...createBlankSpriteLayer(name),
    pixels: Array.from({ length: height }, () => Array(width).fill('#00000000')),
  };
}

// ── Compositing ──────────────────────────────────────────────────────────────
/** Composite all visible layers bottom→top into a flat pixel grid. */
export function compositeLayers(layers: LayerWithPixels[], width: number, height: number): string[][] {
  const result: string[][] = Array.from({ length: height }, () => Array(width).fill('#00000000'));
  for (const layer of layers) {
    if (!layer.visible) continue;
    const alpha = layer.opacity / 100;
    for (let r = 0; r < height; r++) {
      for (let c = 0; c < width; c++) {
        const src = layer.pixels[r]?.[c] ?? '#00000000';
        if (src === '#00000000') continue;
        if (alpha >= 1 && layer.blendMode === 'normal' && !layer.tintColor) {
          result[r][c] = src;
        } else {
          result[r][c] = blendPixel(result[r][c], src, alpha, layer.blendMode, layer.tintColor);
        }
      }
    }
  }
  return result;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = (hex || '#000000').replace('#', '').padEnd(6, '0');
  return [parseInt(h.slice(0, 2), 16) || 0, parseInt(h.slice(2, 4), 16) || 0, parseInt(h.slice(4, 6), 16) || 0];
}
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('');
}

function blendPixel(dst: string, src: string, alpha: number, mode: BlendMode, tint: string | null): string {
  let [sr, sg, sb] = hexToRgb(src);
  if (tint) {
    const [tr, tg, tb] = hexToRgb(tint);
    sr = Math.round(sr * 0.7 + tr * 0.3);
    sg = Math.round(sg * 0.7 + tg * 0.3);
    sb = Math.round(sb * 0.7 + tb * 0.3);
  }
  const [dr, dg, db] = hexToRgb(dst);
  let [or, og, ob] = [sr, sg, sb];
  if (mode === 'multiply')      { or = dr * sr / 255; og = dg * sg / 255; ob = db * sb / 255; }
  else if (mode === 'screen')   { or = 255 - (255 - dr) * (255 - sr) / 255; og = 255 - (255 - dg) * (255 - sg) / 255; ob = 255 - (255 - db) * (255 - sb) / 255; }
  else if (mode === 'overlay')  {
    or = dr < 128 ? 2 * dr * sr / 255 : 255 - 2 * (255 - dr) * (255 - sr) / 255;
    og = dg < 128 ? 2 * dg * sg / 255 : 255 - 2 * (255 - dg) * (255 - sg) / 255;
    ob = db < 128 ? 2 * db * sb / 255 : 255 - 2 * (255 - db) * (255 - sb) / 255;
  }
  return rgbToHex(dr * (1 - alpha) + or * alpha, dg * (1 - alpha) + og * alpha, db * (1 - alpha) + ob * alpha);
}
